import React from "react";
import { motion } from "framer-motion";
import { Linkedin } from "lucide-react";

const team = [
  {
    name: "Arun Velayudhan",
    role: "Founder & CTO",
    credentials: "B.Tech · SMP, IIM Kozhikode",
    bio: "Strategic leader with 25+ years across public safety, AI enablement and productisation — driving digital transformation and secure, mission‑critical applications.",
    initials: "AV",
    tone: "#F2EBE5",
    photo: "/team-arun.jpg",
  },
  {
    name: "Dr. Ajay Velayudhan",
    role: "Co‑Founder & Chief Medical Advisor",
    credentials: "DPM · DipNB (Psych) · FRANZCP",
    bio: "Senior psychiatrist with 30+ years of clinical experience, based in Perth, Western Australia — providing expert leadership and guidance across mental health care services.",
    initials: "AJ",
    tone: "#E6EBE6",
    photo: "/team-ajay.jpg",
  },
  {
    name: "Gora Kundu",
    role: "Chief Strategic Advisor",
    credentials: "M.Tech, IIT Kanpur",
    bio: "Serial entrepreneur with a track record of building R&D organisations from scratch and driving global product development across cross‑functional teams and customers.",
    initials: "GK",
    tone: "#EFE2D6",
    photo: "/team-gora.jpg",
  },
];

export const Team = () => {
  return (
    <section
      id="team"
      className="relative py-24 md:py-32 bg-sentivo-bg"
      data-testid="team-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl mb-16"
        >
          <div className="text-xs tracking-[0.24em] uppercase text-sentivo-secondary mb-6">
            Leadership
          </div>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-sentivo-ink text-balance">
            Built by clinicians,
            <span className="italic text-sentivo-accent"> engineers </span>
            and caregivers.
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
          {team.map((m, i) => (
            <motion.div
              key={m.name}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              whileHover={{ y: -4 }}
              className="group rounded-3xl border border-sentivo-line bg-white p-5 transition-shadow hover:shadow-md"
              data-testid={`team-member-${m.initials.toLowerCase()}`}
            >
              <div
                className="aspect-[4/5] rounded-2xl flex items-end p-5 mb-4 relative overflow-hidden"
                style={{ background: m.tone }}
              >
                <div
                  aria-hidden
                  className="absolute -top-8 -right-8 w-24 h-24 rounded-full opacity-30"
                  style={{
                    background:
                      "radial-gradient(circle, rgba(209,108,77,0.5), transparent 70%)",
                  }}
                />
                {m.photo ? (
                  <img
                    src={m.photo}
                    alt={m.name}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                ) : (
                  <span className="font-serif text-6xl md:text-7xl text-sentivo-primary/30 leading-none">
                    {m.initials}
                  </span>
                )}
              </div>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h3 className="font-serif text-xl text-sentivo-ink leading-tight">
                    {m.name}
                  </h3>
                  <div className="text-xs uppercase tracking-widest text-sentivo-secondary mt-1">
                    {m.role}
                  </div>
                  {m.credentials && (
                    <div className="text-[11px] tracking-wide text-sentivo-accent mt-1.5 font-medium">
                      {m.credentials}
                    </div>
                  )}
                </div>
                <button
                  aria-label={`${m.name} on LinkedIn`}
                  className="text-sentivo-muted hover:text-sentivo-accent transition-colors"
                  data-testid={`team-linkedin-${m.initials.toLowerCase()}`}
                >
                  <Linkedin size={16} />
                </button>
              </div>
              <p className="text-sm text-sentivo-muted leading-relaxed mt-3">{m.bio}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
